import React, { useState } from 'react';
import { X, Upload, AlertCircle, FileText, Trash2 } from 'lucide-react';
import { useThemeColors } from '../theme/ThemeContext';

const DisputeModal = ({
  agreement,
  onClose,
  onSubmit,
  evidenceFiles,
  setEvidenceFiles,
  isLoading
}) => {
  const colors = useThemeColors();
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('');

  const getUserRoles = () => {
    const roles = [];
    if (agreement?.is_sender) roles.push('sender');
    if (agreement?.is_receiver) roles.push('receiver');  
    if (agreement?.is_traveller) roles.push('traveller');
    return roles.length > 0 ? roles : ['receiver'];
  };

  // Dynamic reasons based on user role and agreement status
  const getDisputeReasons = () => {
    const userRoles = getUserRoles();
    const agreementStatus = agreement?.status;

    // Base reasons by role
    const baseReasons = {
      sender: [
        { value: 'traveller_not_picking_up', label: 'Traveller not picking up parcel', status: 'active' },
        { value: 'traveller_not_responding', label: 'Traveller not responding', status: 'in_transit' },
        { value: 'wrong_traveller_assigned', label: 'Wrong traveller assigned', status: 'active' },
        { value: 'pickup_delay', label: 'Pickup delay', status: 'active' },
        { value: 'payment_issue', label: 'Payment or deposit issue', status: 'completed' },
        { value: 'parcel_not_ready', label: 'Parcel not ready for pickup', status: 'active' },
        { value: 'parcel_damaged', label: 'Parcel damaged after pickup' , status: 'in_transit'},
        { value: 'wrong_delivery', label: 'Wrong delivery location' , status: 'completed'},
        { value: 'other', label: 'Other' }
      ],
      traveller: [
        { value: 'sender_not_providing_parcel', label: 'Sender not providing parcel', status: 'active' },
        { value: 'parcel_damaged_on_pickup', label: 'Parcel damaged when received', status: 'active' },
        { value: 'wrong_pickup_location', label: 'Wrong pickup location/address', status: 'active' },
        { value: 'sender_not_responding', label: 'Sender not responding', status: 'active' },
        { value: 'payment_issue', label: 'Payment or deposit issue', status: 'completed' },
        { value: 'parcel_too_large', label: 'Parcel too large/heavy to carry', status: 'active' },
        { value: 'delivery_delay', label: 'Delivery delay' , status: 'in_transit'},
        { value: 'wrong_delivery_location', label: 'Wrong delivery location', status: 'completed'},
        { value: 'other', label: 'Other' }
      ],
      receiver: [
        { value: 'parcel_not_delivered', label: 'Parcel not delivered', status: 'delivered'},
        { value: 'parcel_damaged', label: 'Parcel damaged or tampered with', status: 'delivered' },
        { value: 'wrong_parcel', label: 'Wrong parcel received', status: 'delivered' },
        { value: 'delivery_delay', label: 'Significant delivery delay', status: 'delivered' },
        { value: 'traveller_not_responding', label: 'Traveller not responding', status: 'delivered' },
        { value: 'payment_issue', label: 'Payment or deposit issue', status: 'delivered' },
        { value: 'parcel_not_as_described', label: 'Parcel not as described', status: 'delivered' },
        { value: 'other', label: 'Other', status: 'delivered' }
      ]
    };

    // Combine reasons from ALL user roles
    let allReasons = [];
    userRoles.forEach(role => {
      if (baseReasons[role]) {
        allReasons = allReasons.concat(baseReasons[role]);
      }
    });

    // Remove duplicates based on value
    const uniqueReasons = allReasons.filter((reason, index, self) =>
      index === self.findIndex(r => r.value === reason.value)
    );

    // Filter reasons based on agreement status using the status property
    if (agreementStatus) {
      return uniqueReasons.filter(reason => {
        // If reason has a specific status requirement, check if it matches
        if (reason.status) {
          return reason.status === agreementStatus;
        }
        // If no specific status, show for all statuses
        return true;
      });
    }

    return uniqueReasons;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setEvidenceFiles([...evidenceFiles, ...files]);
  };

  const removeFile = (index) => {
    setEvidenceFiles(evidenceFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!disputeReason || !disputeDescription.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    onSubmit({
      agreement_id: agreement.id,
      reason: disputeReason,
      description: disputeDescription.trim(),
      files: evidenceFiles
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: colors.card,
          borderColor: colors.border
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: colors.border }}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold" style={{ color: colors.text.primary }}>
                Create Dispute
              </h2>
              <p className="text-sm" style={{ color: colors.text.secondary }}>
                Agreement #{agreement?.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            style={{ color: colors.text.secondary }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Agreement Summary */}
          <div className="bg-gray-50 rounded-xl p-4" style={{ backgroundColor: colors.neutral[50] }}>
            <h3 className="font-medium mb-2" style={{ color: colors.text.primary }}>
              Agreement Details
            </h3>
            <div className="text-sm space-y-1" style={{ color: colors.text.secondary }}>
              <p><span className="font-medium capitalize">Status:</span> {agreement?.status?.replace('_', ' ')}</p>
              <p><span className="font-medium">Route:</span>    {agreement.parcel?.pickup_city || agreement.trip?.origin_city || 'Origin'} → {agreement.parcel?.dropoff_city || agreement.trip?.destination_city || 'Destination'}</p>
              <p><span className="font-medium">Your Role:</span> {getUserRoles().join(' & ')}</p>
            </div>
          </div>

          {/* Dispute Reason */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              Reason for Dispute *
            </label>
            <select
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.neutral[50],
                color: colors.text.primary
              }}
              required
            >
              <option value="">Select a reason...</option>
              {getDisputeReasons().map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dispute Description */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              Description *
            </label>
            <textarea
              value={disputeDescription}
              onChange={(e) => setDisputeDescription(e.target.value)}
              placeholder="Please provide detailed information about the issue..."
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 h-32 resize-none"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.neutral[50],
                color: colors.text.primary
              }}
              required
            />
            <p className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
              Be as specific as possible to help resolve the dispute quickly
            </p>
          </div>

          {/* Evidence Upload */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text.primary }}>
              Evidence Files (Optional)
            </label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center" style={{ borderColor: colors.border }}>
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="hidden"
                id="evidence-upload"
              />
              <label htmlFor="evidence-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 mx-auto mb-2" style={{ color: colors.primary[500] }} />
                <p className="text-sm" style={{ color: colors.text.secondary }}>
                  Click to upload evidence files
                </p>
                <p className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
                  Images, PDFs, or documents (max 10MB each)
                </p>
              </label>
            </div>

            {/* File List */}
            {evidenceFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {evidenceFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2" style={{ backgroundColor: colors.neutral[50] }}>
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" style={{ color: colors.primary[500] }} />
                      <span className="text-sm truncate" style={{ color: colors.text.primary }}>
                        {file.name}
                      </span>
                      <span className="text-xs" style={{ color: colors.text.tertiary }}>
                        ({(file.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" style={{ backgroundColor: colors.warning[50], borderColor: colors.warning[200] }}>
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-800" style={{ color: colors.warning[700] }}>
                  Important Notice
                </p>
                <p className="text-sm text-orange-700 mt-1" style={{ color: colors.warning[600] }}>
                  Creating a dispute will pause the agreement and involve platform mediation.
                  Please ensure all information is accurate as disputes cannot be withdrawn once submitted.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t" style={{ borderColor: colors.border }}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
              style={{
                borderColor: colors.border,
                color: colors.text.secondary,
                backgroundColor: 'transparent'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              style={{
                backgroundColor: colors.primary[600],
                color: 'white'
              }}
            >
              {isLoading ? 'Submitting...' : 'Submit Dispute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisputeModal;
